import { useState } from 'react';
import styled from 'styled-components';
import { useGenerateGeoSchemeMutation, useSummarizeGeoSchemeMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  ChecksList,
  ErrorText,
  Hint,
  latestVersion,
  Muted,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  Row,
  SchemeActions,
  Stack,
  useAction,
  VersionDownloads,
  VersionPreview,
  VersionsTable,
} from '@widgets/GeoSchemes';
import { StepShell, useWizard } from './wizard';
import { NotesAssistPanel } from './NotesAssistPanel';

const GENERATE_FROM = new Set(['draft', 'generated', 'rejected']);

const Summary = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  text-wrap: pretty;
`;

/** Шаг 8. Чертёж: генерация версии DXF + PDF + PNG, контроль ГОСТ, версии, резюме. */
export function StepDrawing() {
  const { scheme, readOnly } = useWizard();
  const [generate] = useGenerateGeoSchemeMutation();
  const [summarize] = useSummarizeGeoSchemeMutation();
  const gen = useAction();
  const sum = useAction();
  const [summary, setSummary] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const v = latestVersion(scheme);
  const errors = scheme.checks.filter((c) => c.level === 'error');
  const canGenerate = !readOnly && GENERATE_FROM.has(scheme.status);
  // Отклонения пересчитаны после выпуска версии — в чертеже старые числа.
  const stale = Boolean(v && scheme.deviations && scheme.deviations.computed_at > v.created_at);

  async function makeSummary() {
    setCopied(false);
    const res = await sum.runValue(() => summarize(scheme.id).unwrap(), 'Резюме не получилось.');
    if (res) setSummary(res.summary);
  }

  async function copySummary() {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <StepShell
      n={8}
      lead="Генерация собирает DXF (модель — в метрах натуры, лист — в мм, слои ИС_*), PDF и PNG первого листа. DXF открывается в AutoCAD, nanoCAD, BricsCAD — но для работы CAD не обязателен."
    >
      <Panel aria-labelledby="geo-drawing-title">
        <PanelHead>
          <PanelTitle id="geo-drawing-title">
            {v ? `Версия ${v.version}` : 'Схема ещё не сформирована'}
          </PanelTitle>
          <Row $gap={2}>
            {v && <VersionDownloads scheme={scheme} version={v} />}
            {canGenerate && (
              <Button
                type="button"
                $variant={v ? 'secondary' : 'primary'}
                onClick={() =>
                  void gen.run(() => generate(scheme.id).unwrap(), 'Схема не сформирована.')
                }
                disabled={gen.busy}
              >
                {gen.busy
                  ? 'Формируем DXF и PDF…'
                  : v
                    ? 'Сгенерировать заново'
                    : 'Сгенерировать ИС'}
              </Button>
            )}
          </Row>
        </PanelHead>
        <Stack $gap={3}>
          {gen.error && <ErrorText role="alert">{gen.error}</ErrorText>}
          {stale && (
            <Notice $tone="warning" role="status">
              Отклонения пересчитаны после выпуска версии {v?.version} — сгенерируйте заново, чтобы
              числа на чертеже совпали с расчётом.
            </Notice>
          )}
          {v ? (
            <VersionPreview scheme={scheme} version={v} />
          ) : (
            <Hint>
              Перед генерацией пройдите шаги 1–7: СК и привязка, масштаб, штамп, области, съёмка и
              расчёт отклонений. Сформировать можно и раньше — контроль ниже подскажет, чего не
              хватает.
            </Hint>
          )}
        </Stack>
      </Panel>

      <Panel aria-labelledby="geo-checks-title">
        <PanelHead>
          <PanelTitle id="geo-checks-title">Контроль по ГОСТ Р 51872 (раздел 7)</PanelTitle>
          {!readOnly && <SchemeActions scheme={scheme} actor="geodesist" />}
        </PanelHead>
        <Stack $gap={3}>
          {scheme.checks.length === 0 ? (
            <Notice $tone="success" role="status">
              Замечаний нет — схему можно передавать в ОСНОВА.Инженер.
            </Notice>
          ) : (
            <ChecksList checks={scheme.checks} />
          )}
          {errors.length > 0 && <Muted>Пока есть ошибки, передать схему в ПТО нельзя.</Muted>}
        </Stack>
      </Panel>

      <Panel aria-labelledby="geo-summary-title">
        <PanelHead>
          <PanelTitle id="geo-summary-title">Резюме для ОСНОВА.Инженер</PanelTitle>
          <Row $gap={2}>
            {summary && (
              <Button type="button" $variant="ghost" $size="sm" onClick={() => void copySummary()}>
                {copied ? 'Скопировано' : 'Скопировать'}
              </Button>
            )}
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={() => void makeSummary()}
              disabled={sum.busy || !scheme.deviations}
            >
              {sum.busy ? 'Готовим…' : summary ? 'Обновить' : 'Подготовить резюме'}
            </Button>
          </Row>
        </PanelHead>
        <Stack $gap={3}>
          <Hint>
            Короткий пересказ готового отчёта отклонений — для комментария или письма. Числа в нём
            из расчёта, модель их не меняет.
          </Hint>
          {!scheme.deviations && <Muted>Сначала рассчитайте отклонения на шаге 7.</Muted>}
          {sum.error && <ErrorText role="alert">{sum.error}</ErrorText>}
          {summary && <Summary aria-live="polite">{summary}</Summary>}
        </Stack>
      </Panel>

      <NotesAssistPanel />

      <Panel aria-labelledby="geo-versions-title">
        <PanelHead>
          <PanelTitle id="geo-versions-title">Версии</PanelTitle>
        </PanelHead>
        <VersionsTable scheme={scheme} />
      </Panel>
    </StepShell>
  );
}
